import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_91be65d6-9fee-4eda-abde-3fd83cab62ae/artifacts/kxr3j7eu_IMG_7793.png";

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error('Please enter your email');
      return;
    }
    
    try {
      setLoading(true);
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setSent(true);
      toast.success('Reset link sent!');
    } catch (error) {
      // Always show success to prevent email enumeration
      setSent(true);
      toast.success('Reset link sent!');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="BLK MRKT" className="h-8 w-auto" />
          </Link>
          <Link 
            to="/login"
            className="text-sm font-mono text-tiffany-500 hover:underline"
            data-testid="back-to-login"
          >
            LOGIN
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* Back link */}
          <Link 
            to="/login"
            className="inline-flex items-center gap-2 text-zinc-500 hover:text-tiffany-500 font-mono text-sm mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            BACK TO LOGIN
          </Link>

          {sent ? (
            /* Success State */
            <div className="border border-zinc-800 bg-zinc-900/50 p-8 text-center">
              <CheckCircle className="w-16 h-16 text-tiffany-500 mx-auto mb-6" />
              <h1 className="font-sans text-2xl text-white mb-4">CHECK YOUR EMAIL</h1>
              <p className="text-zinc-400 font-mono text-sm mb-6">
                If an account exists for <span className="text-tiffany-500">{email}</span>, 
                you'll receive a password reset link shortly.
              </p>
              <p className="text-zinc-500 font-mono text-xs mb-8">
                The link expires in 1 hour. Check your spam folder if you don't see it.
              </p>
              <Link to="/login">
                <Button 
                  className="w-full h-12 bg-zinc-800 text-white hover:bg-zinc-700 font-sans tracking-widest rounded-none"
                >
                  RETURN TO LOGIN
                </Button>
              </Link>
            </div>
          ) : (
            /* Form State */
            <div className="border border-zinc-800 bg-zinc-900/50 p-8">
              <div className="flex items-center gap-3 mb-6">
                <Mail className="w-6 h-6 text-tiffany-500" />
                <h1 className="font-sans text-2xl text-white">FORGOT PASSWORD</h1>
              </div>
              
              <p className="text-zinc-400 font-mono text-sm mb-8">
                Enter your email address and we'll send you a link to reset your password.
              </p>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-zinc-500 font-mono text-xs uppercase tracking-wider mb-2">
                    EMAIL ADDRESS
                  </label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full h-12 bg-zinc-900 border-zinc-800 text-white font-mono focus:border-tiffany-500 rounded-none"
                    data-testid="forgot-email-input"
                    autoFocus
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-tiffany-500 text-black hover:bg-tiffany-400 font-sans tracking-widest rounded-none disabled:opacity-50"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? 'SENDING...' : 'SEND RESET LINK'}
                </Button>
              </form>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
